/**
 * Centralized API configuration with Tauri desktop app support.
 *
 * In Tauri v2 the frontend is served via the custom `tauri://` scheme,
 * which means the build-time REACT_APP_API_BASE_URL may point to a
 * production web server that doesn't accept Tauri origins.
 *
 * This module detects the Tauri runtime and resolves the backend URL
 * accordingly:
 *   1. localStorage override  ("api_base_url")  – handy for QA / dev
 *   2. Build-time env var     (REACT_APP_API_BASE_URL)
 *   3. Fallback               (http://localhost:8000)
 */

const FALLBACK_URL = 'http://localhost:8000';

/**
 * `true` when the page is running inside a Tauri v2 desktop shell.
 * Tauri injects `window.__TAURI_INTERNALS__` before any user script runs.
 */
export const isTauri = Boolean(
  typeof window !== 'undefined' && window.__TAURI_INTERNALS__
);

/**
 * Resolve the API base URL at runtime.
 *
 * @returns {string}  e.g. "http://localhost:8000" or "https://qa-portal.services.dev.centime.com:4443"
 */
function resolveApiBaseUrl() {
  // 1. Runtime override via localStorage (works in both web & Tauri)
  if (typeof window !== 'undefined') {
    const override = localStorage.getItem('api_base_url');
    if (override) return override.replace(/\/+$/, '');
  }

  // 2. Build-time env var (baked in by CRA / react-scripts)
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  if (envUrl) return envUrl.replace(/\/+$/, '');

  // 3. Sensible fallback
  return FALLBACK_URL;
}

/** The resolved API base URL for the current environment. */
export const API_BASE_URL = resolveApiBaseUrl();
